<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { goBack, useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { onMounted, reactive, ref } from 'vue';
import { boolean, object, ref as yupRef, string } from 'yup';

const { tenant, user, roles, permissions } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    user: {
        type: Object,
        required: false,
    },
    roles: {
        type: Array,
        required: true,
    },
    permissions: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const form = reactive({
    isActive: user ? user.isActive : true,
    lastname: user ? user.lastname : '',
    firstname: user ? user.firstname : '',
    email: user ? user.email : '',
    changePassword: false,
    password: '',
    passwordConfirmation: '',
    roleIds: roles.reduce((acc, role) => {
        acc[role.id] = user
            ? user.roles.some((userRole) => userRole.id === role.id)
            : false;
        return acc;
    }, {}),
    permissionIds: initPermissionIds(),
});

const isSubmitting = ref(false);

const validationSchema = object({
    isActive: boolean().required('Ce champ est obligatoire.'),
    firstname: string().required('Ce champ est obligatoire.'),
    lastname: string().required('Ce champ est obligatoire.'),
    email: string()
        .required('Ce champ est obligatoire.')
        .email('Veuillez saisir une adresse email valide.'),
    password: string().when('changePassword', {
        is: true,
        then: () => string().required('Ce champ est obligatoire.'),
    }),
    passwordConfirmation: string().when('changePassword', {
        is: true,
        then: () =>
            string()
                .required('Ce champ est obligatoire.')
                .oneOf(
                    [yupRef('password')],
                    'Les mots de passe ne correspondent pas.',
                ),
    }),
});

function initPermissionIds() {
    let permissionsIds = {};

    for (const key in permissions) {
        permissionsIds[permissions[key].id] = user
            ? user.permissions.some(
                  (userPermission) => userPermission.id === permissions[key].id,
              )
            : false;
    }

    return permissionsIds;
}

function submit() {
    axios
        .post(
            user
                ? route('front.postCustomerUser', {
                      tenant: tenant.id,
                      tenantUserId: user.id,
                  })
                : route('front.postCustomerUser', {
                      tenant: tenant.id,
                  }),
            {
                isActive: form.isActive,
                email: form.email,
                lastname: form.lastname,
                firstname: form.firstname,
                password:
                    form.changePassword || user == null ? form.password : null,
                passwordConfirmation:
                    form.changePassword || user == null
                        ? form.passwordConfirmation
                        : null,
                roleIds: Object.keys(form.roleIds)
                    .filter((key) => form.roleIds[key])
                    .map((key) => Number(key)),
                permissionIds: Object.keys(form.permissionIds)
                    .filter((key) => form.permissionIds[key])
                    .map((key) => Number(key)),
            },
        )
        .then(() => {
            toastSaved();

            goBack();
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(route('front.users'));
        });
}

onMounted(() => {});
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <TabLayout
            :tabs="{
                information: {
                    title: 'Informations',
                    contentSlotName: 'information',
                    forceMount: true,
                },
                roles: {
                    title: 'Rôles',
                    contentSlotName: 'roles',
                    forceMount: true,
                },
                permissions: {
                    title: 'Permissions',
                    contentSlotName: 'permissions',
                    forceMount: true,
                },
            }"
        >
            <template #information>
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="1">
                            <SwitchField
                                v-model="form.isActive"
                                field-name="isActive"
                                field-id="isActive"
                                label="Est actif"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="2">
                            <InputField
                                v-model="form.firstname"
                                label="Prénom"
                                placeholder="Prénom"
                                field-name="firstname"
                                field-id="firstname"
                                type="text"
                                required
                            />

                            <InputField
                                v-model="form.lastname"
                                label="Nom"
                                placeholder="Nom"
                                field-name="lastname"
                                field-id="lastname"
                                type="text"
                                required
                            />
                        </FormGrid>

                        <FormGrid :columns="1">
                            <InputField
                                v-model="form.email"
                                label="Email"
                                placeholder="Email"
                                field-name="email"
                                field-id="email"
                                type="email"
                                required
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <SwitchField
                                v-if="user"
                                v-model="form.changePassword"
                                label="Changer le mot de passe"
                                field-id="changePassword"
                                field-name="changePassword"
                            />
                        </FormGrid>

                        <FormGrid
                            v-if="form.changePassword || user == null"
                            :columns="2"
                        >
                            <InputField
                                v-model="form.password"
                                label="Mot de passe"
                                placeholder="Mot de passe"
                                field-name="password"
                                field-id="password"
                                type="password"
                                required
                            />

                            <InputField
                                v-model="form.passwordConfirmation"
                                label="Confirmation du mot de passe"
                                placeholder="Confirmation du mot de passe"
                                field-name="passwordConfirmation"
                                field-id="passwordConfirmation"
                                type="password"
                                required
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #roles>
                <FormColumn>
                    <FormContent>
                        <ul class="flex flex-col items-start">
                            <li v-for="(role, index) in roles" :key="index">
                                <SwitchField
                                    v-model="form.roleIds[role.id]"
                                    :label="
                                        role.name === 'super-admin-datakepler'
                                            ? 'Super Admin Datakepler'
                                            : role.name === 'admin'
                                              ? 'Admin'
                                              : role.name
                                    "
                                    :field-id="'roleIds[' + role.id + ']'"
                                    :field-name="'roleIds[' + role.id + ']'"
                                    inline
                                />
                            </li>
                        </ul>
                    </FormContent>
                </FormColumn>
            </template>

            <template #permissions>
                <FormColumn>
                    <FormContent>
                        <div class="mb-4 flex justify-end">
                            <ActionButton
                                type="secondary"
                                :href="
                                    route(
                                        'front.projectManagement.customer.updateCustomer',
                                        {
                                            id: tenant.id,
                                        },
                                    ) + '?tab=modules'
                                "
                                target="_blank"
                            >
                                Modifier les modules du client
                            </ActionButton>
                        </div>

                        <ul class="flex flex-col">
                            <li
                                v-for="(permission, key) in permissions"
                                :key="key"
                            >
                                <SwitchField
                                    v-model="form.permissionIds[permission.id]"
                                    :label="permission.moduleFullTitle"
                                    :field-id="
                                        'permissionIds[' + permission.id + ']'
                                    "
                                    :field-name="
                                        'permissionIds[' + permission.id + ']'
                                    "
                                    inline
                                />
                            </li>
                        </ul>
                    </FormContent>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="user"
                    :url="
                        route('front.deleteCustomerUser', {
                            tenant: tenant.id,
                            tenantUserId: user.id,
                        })
                    "
                    :callback="
                        () => {
                            router.visit(
                                route('front.users', {
                                    tab: 'activeCustomers',
                                }),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
